import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  Box,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  FileDownload as FileDownloadIcon,
  ContentCopy as ContentCopyIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import Button from './Button';
import TextField from './TextField';
import { useTranslation } from 'react-i18next';

const JsonPartExport = ({ data, title, type }) => {
  const { t } = useTranslation(['courses', 'common']);
  const [anchorEl, setAnchorEl] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDialogOpen = () => {
    setDialogOpen(true);
    handleClose();
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleExport = () => {
    const filename = `${type}_${title
      .toLowerCase()
      .replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.json`;
    const exportText = JSON.stringify(data, null, 2);
    const blob = new Blob([exportText], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(t('courses:jsonExport.success.exported', { type }));
  };

  const copyToClipboard = () => {
    const exportText = JSON.stringify(data, null, 2);
    navigator.clipboard
      .writeText(exportText)
      .then(() => {
        toast.success(t('courses:jsonExport.success.copied'));
      })
      .catch((err) => {
        toast.error(t('courses:jsonExport.error.copying') + err.message);
      });
  };

  return (
    <>
      <Tooltip title={t('courses:jsonExport.tooltip')}>
        <IconButton onClick={handleClick} size="small">
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={handleDialogOpen}>
          {t('courses:jsonExport.menuItem')}
        </MenuItem>
      </Menu>

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '16px' },
        }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" gap={2}>
            <CodeIcon
              color="primary"
              sx={{ fontSize: 28, display: { xs: 'none', md: 'block' } }}
            />
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {t('courses:jsonExport.title', { type, title })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('courses:jsonExport.description')}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <Typography variant="subtitle1">
              {t('courses:jsonExport.dataExport')}
            </Typography>
            <Tooltip title={t('courses:jsonExport.copyTooltip')}>
              <IconButton onClick={copyToClipboard} size="small">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              color="primary"
              size="medium"
              startIcon={<FileDownloadIcon />}
              onClick={handleExport}
            >
              {t('courses:jsonExport.downloadButton')}
            </Button>
          </Stack>
          <TextField
            multiline
            fullWidth
            rows={12}
            value={JSON.stringify(data, null, 2)}
            InputProps={{ readOnly: true }}
            variant="outlined"
            sx={{ mb: 2 }}
          />
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleDialogClose} variant="outlined" size="large">
            {t('common:actions.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default JsonPartExport;
